import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { taka, formatApiErrorDetail } from "@/lib/api";
import { Modal, Field, inputCls, Th, Td, Empty, fmtDate } from "@/pages/admin/AdminUI";

const empty = { supplier_name: "", supplier_mobile: "", paddy_type: "ব্রি-২৯", quantity: 0, price_per_kg: 0, transport_cost: 0, other_cost: 0, paid_amount: 0, inventory_key: "paddy" };

export default function AdminPurchases() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => { api.get("/admin/purchases").then((r) => setPurchases(r.data)); api.get("/admin/suppliers").then((r) => setSuppliers(r.data)); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const total = (Number(form.quantity) * Number(form.price_per_kg) + Number(form.transport_cost) + Number(form.other_cost)) || 0;

  const save = async () => {
    if (!form.supplier_name) return toast.error("সরবরাহকারীর নাম দিন");
    const payload = { ...form };
    ["quantity", "price_per_kg", "transport_cost", "other_cost", "paid_amount"].forEach((k) => payload[k] = Number(payload[k]) || 0);
    try { await api.post("/admin/purchases", payload); toast.success("ক্রয় রেকর্ড হয়েছে"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#1E3A2B]">ধান ক্রয়</h1>
        <button onClick={() => { setForm(empty); setOpen(true); }} className="bg-[#1E3A2B] text-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="add-purchase-btn"><Plus size={18} /> নতুন ক্রয়</button>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-stone-50 border-b border-stone-200"><tr><Th>ক্রয় নং</Th><Th>সরবরাহকারী</Th><Th>তারিখ</Th><Th>ধান</Th><Th>পরিমাণ</Th><Th>মোট</Th><Th>বাকি</Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {purchases.map((p) => (
              <tr key={p.id} data-testid={`purchase-row-${p.purchase_no}`}>
                <Td className="font-semibold text-[#1E3A2B]">{p.purchase_no}</Td>
                <Td>{p.supplier_name}</Td>
                <Td>{fmtDate(p.date)}</Td>
                <Td>{p.paddy_type}</Td>
                <Td>{p.quantity} কেজি</Td>
                <Td className="font-semibold">{taka(p.total)}</Td>
                <Td className={p.due > 0 ? "text-red-600 font-semibold" : "text-emerald-700"}>{taka(p.due)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        {purchases.length === 0 && <Empty text="কোনো ক্রয় রেকর্ড নেই" />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="নতুন ধান ক্রয়" wide>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="সরবরাহকারী (তালিকা থেকে)">
            <select className={inputCls} onChange={(e) => { const s = suppliers.find((x) => x.id === e.target.value); if (s) { set("supplier_id", s.id); set("supplier_name", s.name); set("supplier_mobile", s.mobile); } }} data-testid="pur-supplier-select">
              <option value="">অথবা নতুন লিখুন</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="সরবরাহকারীর নাম"><input className={inputCls} value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} data-testid="pur-supplier-name" /></Field>
          <Field label="মোবাইল"><input className={inputCls} value={form.supplier_mobile} onChange={(e) => set("supplier_mobile", e.target.value)} /></Field>
          <Field label="ধানের ধরন"><input className={inputCls} value={form.paddy_type} onChange={(e) => set("paddy_type", e.target.value)} /></Field>
          <Field label="পরিমাণ (কেজি)"><input type="number" className={inputCls} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="pur-qty" /></Field>
          <Field label="দর (৳/কেজি)"><input type="number" className={inputCls} value={form.price_per_kg} onChange={(e) => set("price_per_kg", e.target.value)} data-testid="pur-price" /></Field>
          <Field label="পরিবহন খরচ"><input type="number" className={inputCls} value={form.transport_cost} onChange={(e) => set("transport_cost", e.target.value)} /></Field>
          <Field label="অন্যান্য খরচ"><input type="number" className={inputCls} value={form.other_cost} onChange={(e) => set("other_cost", e.target.value)} /></Field>
          <Field label="পরিশোধিত (৳)"><input type="number" className={inputCls} value={form.paid_amount} onChange={(e) => set("paid_amount", e.target.value)} data-testid="pur-paid" /></Field>
        </div>
        <div className="mt-3 bg-emerald-50 rounded-xl p-3 flex justify-between font-semibold text-emerald-900"><span>মোট: {taka(total)}</span><span>বাকি: {taka(total - Number(form.paid_amount))}</span></div>
        <button onClick={save} className="mt-4 w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="save-purchase-btn">সংরক্ষণ (স্টকে যোগ হবে)</button>
      </Modal>
    </div>
  );
}
